import React from 'react';
import { Question } from '../types/quiz';

interface QuizCardProps {
  question: Question;
  selectedAnswers: number[];
  onAnswerSelect: (selectedIndices: number[]) => void;
}

export default function QuizCard({ 
  question, 
  selectedAnswers = [], 
  onAnswerSelect 
}: QuizCardProps) {
  const handleOptionClick = (index: number) => {
    let newSelectedAnswers: number[];
    
    if (selectedAnswers.includes(index)) {
      // If already selected, remove it
      newSelectedAnswers = selectedAnswers.filter(i => i !== index);
    } else {
      // If not selected, add it
      newSelectedAnswers = [...selectedAnswers, index];
    }
    
    onAnswerSelect(newSelectedAnswers);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-4">
        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
          question.level === 'easy' 
            ? 'bg-green-100 text-green-800'
            : question.level === 'medium'
            ? 'bg-yellow-100 text-yellow-800'
            : 'bg-red-100 text-red-800'
        }`}>
          {question.level.charAt(0).toUpperCase() + question.level.slice(1)}
        </span>
        <span className="ml-2 text-sm text-gray-500">
          {question.marks} {question.marks === 1 ? 'mark' : 'marks'}
        </span>
      </div>

      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        {question.text}
      </h3>

      <div className="space-y-3">
        {question.options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleOptionClick(index)}
            className={`w-full text-left p-4 rounded-lg transition-all duration-200 ${
              selectedAnswers.includes(index)
                ? 'bg-purple-100 border-2 border-purple-500 text-purple-900'
                : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center">
              <div className={`w-6 h-6 rounded-full border-2 mr-3 flex items-center justify-center ${
                selectedAnswers.includes(index)
                  ? 'border-purple-500 bg-purple-500'
                  : 'border-gray-300'
              }`}>
                {selectedAnswers.includes(index) && (
                  <svg 
                    className="w-4 h-4 text-white" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M5 13l4 4L19 7" 
                    />
                  </svg>
                )}
              </div>
              <span className={`${
                selectedAnswers.includes(index)
                  ? 'text-purple-900 font-medium'
                  : 'text-gray-700'
              }`}>
                {option}
              </span>
            </div>
          </button>
        ))}
      </div>

      {question.correctAnswers.length > 1 && (
        <p className="mt-4 text-sm text-gray-500 italic">
          * This question has multiple correct answers
        </p>
      )}
    </div>
  );
}
